import type React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import { useSelector } from "react-redux";
import apiService from "../../../../service/Api";
import { RootState } from "../../../../store/Index";

interface IntroductionLetterTemplate {
  id: number;
  name: string;
  category: string;
  category_display: string;
  body_template: string;
  placeholders: string[];
  is_active: boolean;
}

interface TemplateSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (template: string) => void;
  bookingType: "FUNERAL" | "EVENT" | "CONFERENCE";
  proposalData?: {
    clientName?: string;
    deceasedName?: string;
    eventTitle?: string;
    deceasedBirthDate?: string;
    deceasedPassedDate?: string;
    serviceVenue?: string;
    serviceDate?: string;
  };
}

const TemplateSelectionModal: React.FC<TemplateSelectionModalProps> = ({
  open,
  onClose,
  onApply,
  bookingType,
  proposalData,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const [templates, setTemplates] = useState<IntroductionLetterTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] =
    useState<IntroductionLetterTemplate | null>(null);

  const renderTemplateWithBoldVariables = (template: string): string => {
    if (!proposalData) return template;

    let result = template;

    const replacements: Record<string, string> = {
      "{client_name}": proposalData.clientName || "{client_name}",
      "{deceased_name}": proposalData.deceasedName || "{deceased_name}",
      "{event_title}": proposalData.eventTitle || "{event_title}",
      "{date_of_birth}": proposalData.deceasedBirthDate || "{date_of_birth}",
      "{date_of_death}": proposalData.deceasedPassedDate || "{date_of_death}",
      "{service_venue}": proposalData.serviceVenue || "{service_venue}",
      "{service_date}": proposalData.serviceDate || "{service_date}",
      "{current_date}": new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    };

    Object.entries(replacements).forEach(([placeholder, value]) => {
      const replacement =
        value !== placeholder ?  `<strong style="color: black;">${value}</strong>` : placeholder;
      result = result.replace(
        new RegExp(placeholder.replace(/[{}]/g, "\\$&"), "g"),
        replacement,
      );
    });

    return result;
  };

  const normalizedCategory = bookingType.toLowerCase();

  const filteredTemplates = templates.filter(
    (template) => template.category === normalizedCategory,
  );

  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get<
        IntroductionLetterTemplate[] | { results: IntroductionLetterTemplate[] }
      >("/api/configurations/introduction-letter-templates/");
      const data = response.data;
      setTemplates(Array.isArray(data) ? data : data.results);
    } catch (err: unknown) {
      console.error("Failed to fetch templates:", err);
      setError(
        (err as { message?: string })?.message || "Failed to load templates",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open]);

  const handleTemplateSelect = (template: IntroductionLetterTemplate) => {
    setSelectedTemplate(template);
  };

  const handleApplyTemplate = () => {
    if (selectedTemplate) {
      onApply(selectedTemplate.body_template);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedTemplate(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { backgroundColor: reduxTheme.primary_colors[900] },
      }}
    >
      <DialogTitle sx={{ color: reduxTheme.primary_colors[100] }}>
        Select Introduction Letter Template
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress sx={{ color: reduxTheme.primary_colors[400] }} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 3,
              minHeight: "300px",
            }}
          >
            <Box>
              <List sx={{ p: 0 }}>
                {filteredTemplates.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography sx={{ color: reduxTheme.primary_colors[300] }}>
                      No {bookingType.toLowerCase()} templates available.
                    </Typography>
                  </Box>
                ) : (
                  filteredTemplates.map((template) => (
                    <ListItem key={template.id} disablePadding>
                      <ListItemButton
                        selected={selectedTemplate?.id === template.id}
                        onClick={() => handleTemplateSelect(template)}
                        sx={{
                          borderRadius: 2,
                          "&.Mui-selected": {
                            backgroundColor: `${reduxTheme.primary_colors[400]}20`,
                            borderLeft: `2px solid ${reduxTheme.primary_colors[400]}`,
                          },
                          "&:hover": {
                            backgroundColor: `${reduxTheme.primary_colors[400]}10`,
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Typography
                                sx={{ color: reduxTheme.primary_colors[100] }}
                              >
                                {template.name}
                              </Typography>
                              <Chip
                                label={template.category_display}
                                size="small"
                                sx={{
                                  backgroundColor:
                                    reduxTheme.primary_colors[500],
                                  color: reduxTheme.primary_colors[900],
                                  fontSize: "0.7rem",
                                }}
                              />
                            </Box>
                          }
                          secondary={
                            <Typography
                              variant="body2"
                              component="div"
                              sx={{
                                color: reduxTheme.primary_colors[300],
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                "& strong": {
                                  color: reduxTheme.primary_colors[400],
                                  fontWeight: 700,
                                },
                              }}
                              dangerouslySetInnerHTML={{
                                __html:
                                  renderTemplateWithBoldVariables(
                                    template.body_template,
                                  ).substring(0, 200) + "...",
                              }}
                            />
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))
                )}
              </List>
            </Box>

            <Box
              sx={{
                border: `1px solid ${reduxTheme.primary_colors[700]}`,
                borderRadius: 2,
                p: 2,
                backgroundColor: reduxTheme.primary_colors[800],
                overflowY: "auto",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ color: reduxTheme.primary_colors[300], mb: 1 }}
              >
                Preview
              </Typography>

              {selectedTemplate ? (
                <Typography
                  component="div"
                  sx={{
                    whiteSpace: "pre-line",
                    lineHeight: 1.7,
                    color: reduxTheme.primary_colors[100],
                    "& strong": {
                      color: reduxTheme.primary_colors[400],
                      fontWeight: 700,
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: renderTemplateWithBoldVariables(
                      selectedTemplate.body_template,
                    ),
                  }}
                />
              ) : (
                <Typography sx={{ color: reduxTheme.primary_colors[400] }}>
                  Select a template to preview it
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClose}
          sx={{ color: reduxTheme.primary_colors[300] }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleApplyTemplate}
          variant="contained"
          disabled={!selectedTemplate}
          sx={{
            backgroundColor: reduxTheme.primary_colors[400],
            color: reduxTheme.primary_colors[900],
            "&:hover": {
              backgroundColor: reduxTheme.primary_colors[500],
            },
          }}
        >
          Apply Template
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateSelectionModal;
